import { useState, useCallback, useMemo } from "react";
import { message, Modal } from "antd";
import { ROLES, STAGES } from "components/common/Constants";
import { getFormalName, getNextStage } from "components/services";
import { url as API_BASE_URL } from "components/common/api";


// Custom hook for managing modal states
export const useModalStates = () => {
	const [digitizerValidationModal, setDigitizerValidationModal] = useState({
		visible: false,
		record: null,
		incompleteVersions: [],
	});
	const [courierModeValidationModal, setCourierModeValidationModal] = useState({
		visible: false,
		record: null,
		incompleteVersions: [],
	});
	const [invoiceValidationModal, setInvoiceValidationModal] = useState({
		visible: false,
		record: null,
		incompleteVersions: [],
	});
	const [confirmModal, setConfirmModal] = useState({
		visible: false,
		record: null,
		nextStage: null,
	});
	const [infoModal, setInfoModal] = useState({
		visible: false,
		jobcardId: null,
	});
	const [completedInfoModal, setCompletedInfoModal] = useState({
		visible: false,
		jobcardId: null,
	});
	const [activityLogModal, setActivityLogModal] = useState({
		visible: false,
		jobcardId: null,
	});

	const closeDigitizerModal = useCallback(
		() => setDigitizerValidationModal({ visible: false, record: null, incompleteVersions: [] }),
		[]
	);
	const closeCourierModal = useCallback(
		() => setCourierModeValidationModal({ visible: false, record: null, incompleteVersions: [] }),
		[]
	);
	const closeInvoiceModal = useCallback(
		() => setInvoiceValidationModal({ visible: false, record: null, incompleteVersions: [] }),
		[]
	);
	const closeConfirmModal = useCallback(
		() => setConfirmModal({ visible: false, record: null, nextStage: null }),
		[]
	);
	const closeInfoModal = useCallback(() => setInfoModal({ visible: false, jobcardId: null }), []);
	const closeCompletedInfoModal = useCallback(
		() => setCompletedInfoModal({ visible: false, jobcardId: null }),
		[]
	);
	const closeActivityLogModal = useCallback(
		() => setActivityLogModal({ visible: false, jobcardId: null }),
		[]
	);

	return {
		digitizerValidationModal,
		setDigitizerValidationModal,
		closeDigitizerModal,
		courierModeValidationModal,
		setCourierModeValidationModal,
		closeCourierModal,
		invoiceValidationModal,
		setInvoiceValidationModal,
		closeInvoiceModal,
		confirmModal,
		setConfirmModal,
		closeConfirmModal,
		infoModal,
		setInfoModal,
		closeInfoModal,
		completedInfoModal,
		setCompletedInfoModal,
		closeCompletedInfoModal,
		activityLogModal,
		setActivityLogModal,
		closeActivityLogModal,
	};
};

// Custom hook for forward validation logic
export const useForwardValidation = ({
	user,
	setDigitizerValidationModal,
	setCourierModeValidationModal,
	setInvoiceValidationModal,
	setConfirmModal,
	navigate,
}) => {
	const validateAndForward = useCallback(
		async (record) => {
			// Creation stage to cutting - digitizer assignment check
			const needsDigitizerAssignment = record.current_stage === STAGES.CREATION &&
				(record.is_degitizer_stage === true || record.is_degitizer_stage === 'true') &&
				(!record.assigned_digitizers || record.assigned_digitizers.length === 0);

			if (needsDigitizerAssignment && navigate) {
				navigate(`/jobcard/update/${record.id}`, { state: { autoFocusDigitizer: true } });
				return false;
			}

			// Packing stage validation - courier mode required
			const isPackingForwardAttempt =
				user.roll === ROLES.PACKING && record.current_stage === STAGES.PACKING;

			if (isPackingForwardAttempt && !record.courier_mode) {
				Modal.warning({
					title: "Courier Mode Required",
					content:
						"Please select a courier mode in the jobcard details before forwarding to Billing.",
				});
				return false;
			}

			// Account stage validation - invoice required
			if (user.roll === ROLES.ACCOUNT && record.current_stage === STAGES.ACCOUNT) {
				try {
					const res = await fetch(`${API_BASE_URL}/api/invoices/${record.id}/`, {
						method: "GET",
						headers: {
							Accept: "application/json",
							Authorization: `Bearer ${localStorage.getItem("userToken")}`,
						},
					});
					if (!res.ok) {
						throw new Error("Failed to fetch invoices");
					}
					const invoices = await res.json();
					if (!Array.isArray(invoices) || invoices.length === 0) {
						Modal.warning({
							title: "Invoice Required",
							content:
								"Please upload at least one invoice before forwarding this jobcard to Payment.",
						});
						return false;
					}
				} catch (error) {
					Modal.error({
						title: "Unable to verify invoices",
						content:
							"Could not verify invoices for this jobcard. Please try again after reopening the page.",
					});
					return false;
				}
			}

			// Secondary process to embroidary - digitizer validation
			if (record.current_stage === STAGES.SECONDARY_PROCESS) {
				const nextStage = getNextStage(record.current_stage);
				if (nextStage === STAGES.EMBROIDARY) {
					if (
						record?.is_degitizer_stage === true ||
						record?.is_degitizer_stage === "true"
					) {
						setDigitizerValidationModal({
							visible: true,
							record: record,
							incompleteVersions: [record],
						});
						return false;
					}
				}
			}

			// All validations passed, show confirm modal
			const nextStage = getNextStage(record.current_stage);

			setConfirmModal({
				visible: true,
				record: record,
				nextStage: nextStage,
			});
			return true;
		},
		[user, setDigitizerValidationModal, setConfirmModal]
	);

	return { validateAndForward };
};

// Custom hook for handling confirm forward action
export const useConfirmForward = ({
	confirmModal,
	setConfirmModal,
	setDigitizerValidationModal,
	setCourierModeValidationModal,
	setInvoiceValidationModal,
	updateJobcard,
	dispatch,
	allApi,
}) => {
	const handleConfirmForward = useCallback(async () => {
		const record = confirmModal.record;
		const nextStage = confirmModal.nextStage;

		if (!record || !nextStage) {
			message.error("Invalid job card data");
			setConfirmModal({ visible: false, record: null, nextStage: null });
			return;
		}

		const updateData = {
			id: record.id,
			current_stage: nextStage,
		};

		// Backend now dynamically computes is_degitizer_stage when forwarding to cutting
		// based on whether all required sides already have embroidery files attached

		try {
			await updateJobcard(updateData).unwrap();
			dispatch(
				allApi.util.invalidateTags([
					"Card",
					"CardCompleted",
					"Topcard",
					"CLIENT_COMPANY",
					"CLIENT_COMPANY_COMPLETED",
				])
			);
			setConfirmModal({ visible: false, record: null, nextStage: null });

			const stageName = getFormalName(nextStage);
			message.success(
				nextStage === STAGES.COMPLETED
					? "Job card marked as completed"
					: `Job card forwarded to ${stageName}`
			);
		} catch (error) {
			if (process.env.NODE_ENV === "development") {
				console.log("Forward error caught:", error);
			}

			const errorCode = error?.data?.error_code;
			const errorDetail = error?.data?.detail || "";

			if (
				errorCode === "DIGITIZER_WORK_REMAINING" ||
				errorDetail.toLowerCase().includes("digitizer")
			) {
				setConfirmModal({ visible: false, record: null, nextStage: null });
				setDigitizerValidationModal({
					visible: true,
					record: record,
					incompleteVersions: error?.data?.incomplete_versions || [],
				});
			} else if (errorCode === "COURIER_MODE_REQUIRED") {
				setConfirmModal({ visible: false, record: null, nextStage: null });
				setCourierModeValidationModal({
					visible: true,
					record: record,
					incompleteVersions: error?.data?.incomplete_versions || [],
				});
			} else if (errorCode === "INVOICE_REQUIRED") {
				setConfirmModal({ visible: false, record: null, nextStage: null });
				setInvoiceValidationModal({
					visible: true,
					record: record,
					incompleteVersions: error?.data?.incomplete_versions || [],
				});
			} else {
				const errorMessage =
					error?.data?.detail || error?.data?.message || "Failed to forward job card";
				message.error(errorMessage);
				setConfirmModal({ visible: false, record: null, nextStage: null });
			}
		}
	}, [
		confirmModal,
		setConfirmModal,
		setDigitizerValidationModal,
		setCourierModeValidationModal,
		setInvoiceValidationModal,
		updateJobcard,
		dispatch,
		allApi,
	]);

	return { handleConfirmForward };
};

// Custom hook for dispatch checkbox handling
export const useDispatchSelection = ({ setSelectedRowKeys, setRecordBig }) => {
	const handleDispatchCheckboxChange = useCallback(
		(record, checked) => {
			if (typeof setSelectedRowKeys !== "function") return;

			setSelectedRowKeys((prev = []) => {
				const prevKeys = Array.isArray(prev) ? prev : [];
				if (checked) {
					if (prevKeys.includes(record.id)) return prevKeys;
					return [...prevKeys, record.id];
				}
				return prevKeys.filter((key) => key !== record.id);
			});

			if (typeof setRecordBig === "function") {
				setRecordBig((prevRecords = []) => {
					const prevList = Array.isArray(prevRecords) ? prevRecords : [];
					if (checked) {
						if (prevList.some((item) => item.id === record.id)) return prevList;
						return [...prevList, record];
					}
					return prevList.filter((item) => item.id !== record.id);
				});
			}
		},
		[setSelectedRowKeys, setRecordBig]
	);

	return { handleDispatchCheckboxChange };
};

// Custom hook for client and jobcard maps
export const useDataMaps = ({ clientsList, cards }) => {
	const clientMap = useMemo(() => {
		const map = new Map();
		(clientsList || []).forEach((client) => {
			if (client?.id) {
				map.set(client.id, client);
			}
		});
		return map;
	}, [clientsList]);

	const jobcardMap = useMemo(() => {
		const map = new Map();
		(cards || []).forEach((card) => {
			if (card?.id) {
				map.set(card.id, card);
			}
		});
		return map;
	}, [cards]);

	return { clientMap, jobcardMap };
};
